import CarouselCardSmall from 'components/OfferList/OfferCards/OfferCardSmall/CarouselCardSmall'
import type { ComponentProps } from 'react'
import BookmarkButton from 'wishlists/components/Bookmark/Buttons'
import { getLocationString } from 'checkout/lib/utils/accommodation/location'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import LEOfferPriceDetails from './LEOfferPriceDetails'
import { isCruiseV1Offer } from 'lib/offer/offerTypes'
import CruiseFlashPriceDetails from './CruiseFlashPriceDetails'
import CruiseDescription from './CruiseDescription'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { isUltraLuxOffer } from 'lib/offer/offerUtils'
import CarouselCardMedium from './OfferCardMedium/CarouselCardMedium'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import AgentHubAdditionalCommissionLabel from 'agentHub/components/AgentHubAdditionalCommissionLabel'
import AgentHubEarnYourOwnTourLabel from 'agentHub/components/AgentHubEarnYourOwnTourLabel'
import { useIntl } from 'react-intl'
import { useRebrandOverlayBookmarkProps } from 'hooks/useRebrandOverlayButtonProps'

type InheritedCarouselCardMediumProps = Pick<ComponentProps<typeof CarouselCardMedium>, 'variant' | 'eagerLoadImage'>

interface Props extends InheritedCarouselCardMediumProps {
  offer: App.TourOffer | App.TourOfferSummary
  onClick: (isRightClick?: boolean) => void
  className?: string
  offerUrl: string
  size: 'small' | 'medium'
}

function TourOfferCard(props: Props) {
  const intl = useIntl()
  const { iconKind, iconVariant } = useRebrandOverlayBookmarkProps()
  const {
    offer,
    onClick,
    className,
    offerUrl,
    size,
    variant,
    eagerLoadImage,
  } = props
  const isUltraLux = isUltraLuxOffer(offer)

  const agentExclusiveLabel = intl.formatMessage({
    id: 'ZKOtQOuSMK5FG-meK6Hk',
    defaultMessage: 'Agent Exclusive',
    description: 'Badge label for agent hub exclusive tour offer in the offer listing section',
  })

  return (
    <ProductPaletteProvider product={offer}>
      {size === 'medium' && <CarouselCardMedium
        variant={variant}
        className={className}
        eagerLoadImage={eagerLoadImage}
        bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" iconKind={iconKind} iconVariant={iconVariant} />}
        image={offer.image}
        images={offer.images}
        location={getLocationString(intl, offer)}
        title={offer.name}
        providerName={offer.vendorName}
        to={offerUrl}
        onClick={onClick}
        imageCornerLabel={<AgentHubEarnYourOwnTourLabel offerId={offer.id} />}
        urgencyLabels={<LabelGroup>
          {isUltraLux && <ProductTypeLabel productType={offer.productType} />}
          {offer.isAgentHubExclusive && <PromotionLabel key="agenthub_exclusive">{agentExclusiveLabel}</PromotionLabel>}
          <AgentHubAdditionalCommissionLabel offerId={offer?.id} />
          {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
          {offer?.availabilitySchedule?.end && (
            <OfferUrgencyCountdownLabel
              endDate={offer.availabilitySchedule.end}
              tagPlace="offer-main"
            />
          )}
        </LabelGroup>}
        priceDetails={
          isCruiseV1Offer(offer) ?
              <CruiseFlashPriceDetails variant="condensed" offer={offer} /> :
              <LEOfferPriceDetails variant="condensed" offer={offer} />
        }
        description={
          isCruiseV1Offer(offer) ?
              <CruiseDescription variant="condensed" offer={offer} /> :
            undefined
        }
      />}
      {size === 'small' && <CarouselCardSmall
        variant={variant}
        className={className}
        eagerLoadImage={eagerLoadImage}
        bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" iconKind={iconKind} iconVariant={iconVariant} />}
        image={offer.image}
        images={offer.images}
        location={getLocationString(intl, offer)}
        title={offer.name}
        to={offerUrl}
        onClick={onClick}
        imageCornerLabel={<AgentHubEarnYourOwnTourLabel offerId={offer.id} />}
        urgencyLabels={<LabelGroup>
          {offer.isAgentHubExclusive && <PromotionLabel key="agenthub_exclusive">{agentExclusiveLabel}</PromotionLabel>}
          <AgentHubAdditionalCommissionLabel offerId={offer?.id} />
          {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
        </LabelGroup>}
        description={
          isCruiseV1Offer(offer) ?
              <CruiseDescription offer={offer} /> :
            undefined
        }
        priceDetails={
          isCruiseV1Offer(offer) ?
              <CruiseFlashPriceDetails offer={offer} /> :
              <LEOfferPriceDetails offer={offer} />
        }
      />}
    </ProductPaletteProvider>
  )
}

export default TourOfferCard
